// src/pages/dashboards/user/UserOverview.jsx
// Personal hub: scan-pool gauge, deductions, claims status, recent receipts.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getScanUsage, expenseSummary, listClaims, listReceipts,
  listPractitioners, unlinkPractitioner, redeemCode,
} from "../../../api/user";
import { Card, Loading, Select, Badge, Button, Input, Modal, Field } from "../../../components/ui";
import { DonutChart, GaugeChart, HBarChart } from "../../../components/charts";
import { money, titleCase } from "../../../lib/format";

const CAT_COLORS = ["#12B266", "#2AD17B", "#3B82F6", "#F5A623", "#8A938F", "#0EA5A0", "#A855F7", "#EF4444"];

// SA tax year: 1 Mar (Y-1) - 28/29 Feb (Y). March onward belongs to next year.
const currentTaxYear = () => { const d = new Date(); return d.getMonth() >= 2 ? d.getFullYear() + 1 : d.getFullYear(); };
const TY = currentTaxYear();
const TAX_YEARS = [TY, TY - 1, TY - 2, TY - 3];

// small labelled tile sitting inside a card
function Stat({ label, value, accent, className = "" }) {
  return (
    <div className={`rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-4 py-3 text-center ${className}`}>
      <p className="text-[11px] uppercase tracking-wider text-tb-muted">{label}</p>
      <p className={`text-xl sm:text-2xl font-bold leading-none mt-1.5 tabular-nums whitespace-nowrap ${accent ? "text-tb-brand" : ""}`}>{value}</p>
    </div>
  );
}

export default function UserOverview() {
  const [usage, setUsage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [claims, setClaims] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [taxYear, setTaxYear] = useState(TY);
  const [err, setErr] = useState("");

  // practitioner linking
  const [practitioners, setPractitioners] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const loadPractitioners = () =>
    listPractitioners().then(setPractitioners).catch(() => setPractitioners([]));

  useEffect(() => {
    Promise.all([getScanUsage(), listClaims(), listReceipts()])
      .then(([u, c, r]) => { setUsage(u); setClaims(c); setReceipts(r); })
      .catch((e) => setErr(e.message));
    loadPractitioners();
  }, []);

  useEffect(() => {
    expenseSummary({ tax_year: taxYear }).then(setSummary).catch((e) => setErr(e.message));
  }, [taxYear]);

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!usage || !summary) return <Loading label="Loading your dashboard" />;

  const unlimited = usage.limit == null;
  const remaining = usage.remaining != null ? usage.remaining : (unlimited ? null : (usage.limit - (usage.used || 0)));
  const credits = usage.credits || 0;
  const totalAvailable = unlimited ? null : remaining + credits;
  const remainPct = unlimited ? 1 : (usage.limit ? remaining / usage.limit : 0);
  const gaugeColor = remainPct <= 0.15 ? "#EF4444" : remainPct <= 0.35 ? "#F5A623" : "#12B266";

  const outstanding = claims.filter((c) => ["submitted", "queried"].includes(c.status));
  const paid = claims.filter((c) => c.status === "paid");
  const outstandingAmt = outstanding.reduce((s, c) => s + (c.total || 0), 0);
  const paidAmt = paid.reduce((s, c) => s + (c.total || 0), 0);
  const claimsData = [
    { label: `Outstanding (${outstanding.length})`, value: outstandingAmt, color: "#F5A623" },
    { label: `Paid (${paid.length})`, value: paidAmt, color: "#12B266" },
  ].filter((d) => d.value > 0);

  const cats = (summary.categories || [])
    .map((c, i) => ({ label: c.name, value: Number(c.total || 0), color: CAT_COLORS[i % CAT_COLORS.length] }))
    .filter((c) => c.value > 0);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Overview</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* scan-pool gauge */}
        <Card className="p-6 lg:col-span-1 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-tb-muted">Scan pool</p>
            <Badge tone="gray">{titleCase(usage.tier || "free")}</Badge>
          </div>

          <div className="flex-1 flex items-center justify-center py-2">
            <GaugeChart
              value={unlimited ? 1 : remaining}
              max={unlimited ? null : usage.limit}
              centerValue={unlimited ? "\u221e" : totalAvailable}
              centerLabel="scans left"
              color={gaugeColor}
              credits={unlimited ? 0 : credits}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-1">
            <Stat label="Used" value={usage.used ?? 0} />
            <Stat label="Limit" value={unlimited ? "\u221e" : usage.limit} />
          </div>

          {credits > 0 && (
            <div className="mt-3 flex items-center justify-center gap-4 text-[11px]">
              <span className="inline-flex items-center gap-1.5 text-tb-muted">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: gaugeColor }} />
                Plan ({unlimited ? "\u221e" : remaining})
              </span>
              <span className="inline-flex items-center gap-1.5 text-tb-muted">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#5FD0C0" }} />
                Top-up ({credits})
              </span>
            </div>
          )}
        </Card>

        {/* deductions */}
        <Card className="p-6 lg:col-span-2 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-wider text-tb-muted">Deductions · tax year {taxYear}</p>
            <Select value={taxYear} onChange={(e) => setTaxYear(parseInt(e.target.value, 10))} className="w-auto">
              {TAX_YEARS.map((y) => <option key={y} value={y}>TY {y}</option>)}
            </Select>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat label="Claimed total" value={money(summary.grand_total)} accent className="col-span-2 sm:col-span-1" />
            <Stat label="Items claimed" value={summary.claimed_count ?? 0} />
            <Stat label="Receipts" value={receipts.length} />
          </div>

          <div className="pt-5 mt-5 border-t border-tb-border dark:border-tb-d-border flex-1">
            <p className="text-sm font-semibold mb-3">Claimed by category</p>
            {cats.length ? <HBarChart data={cats} valueFormat={money} />
              : <p className="text-sm text-tb-muted">No claimed expenses yet this tax year.</p>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* claims status */}
        <Card className="p-6">
          <p className="text-sm font-semibold mb-4">Company claims</p>
          {claimsData.length ? (
            <DonutChart data={claimsData} valueFormat={money}
                        center={{ value: claims.length, label: "claims" }} />
          ) : <p className="text-sm text-tb-muted">No claims yet.</p>}
        </Card>

        {/* recent receipts */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold">Recent receipts</p>
            <Link to="/app/receipts" className="text-xs text-tb-brand hover:underline">View all</Link>
          </div>
          {receipts.length === 0 && <p className="text-sm text-tb-muted">No receipts yet.</p>}
          <div className="space-y-2">
            {receipts.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.merchant || "Unknown merchant"}</p>
                  <p className="text-xs text-tb-muted">{r.item_count} item{r.item_count === 1 ? "" : "s"}</p>
                </div>
                <span className="text-sm font-medium tabular-nums ml-3 shrink-0">{money(r.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* tax practitioner */}
      <div className="mt-5">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold">Tax practitioner</p>
            <Button variant="ghost" onClick={() => setShowLinkModal(true)} className="text-xs">
              {practitioners.length ? "Manage" : "Link practitioner"}
            </Button>
          </div>
          {practitioners.length === 0 ? (
            <p className="text-sm text-tb-muted">
              Link your tax practitioner so they can view your deductions and receipts at tax time.
            </p>
          ) : (
            <div className="space-y-2">
              {practitioners.map((p) => (
                <div key={p.link_id} className="flex items-center justify-between rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-tb-muted truncate">{p.email}</p>
                  </div>
                  <Badge tone="green">linked</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <LinkPractitionerModal
        open={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        practitioners={practitioners}
        onChanged={() => { setShowLinkModal(false); loadPractitioners(); }}
      />
    </div>
  );
}

// -- Link practitioner modal --------------------------------------------------
function LinkPractitionerModal({ open, onClose, practitioners, onChanged }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (open) { setCode(""); setError(""); setSuccess(""); }
  }, [open]);

  const handleRedeem = async () => {
    setError(""); setSuccess("");
    if (!code.trim()) { setError("Enter a connect code."); return; }
    setBusy(true);
    try {
      await redeemCode(code.trim().toUpperCase());
      setSuccess("Practitioner linked successfully.");
      setTimeout(onChanged, 800);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async (linkId) => {
    if (!window.confirm("Remove this practitioner's access to your deductions?")) return;
    try {
      await unlinkPractitioner(linkId);
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Tax practitioner">
      <div className="space-y-5">
        {/* existing links */}
        {practitioners.length > 0 && (
          <div>
            <p className="text-xs font-medium text-tb-muted uppercase tracking-wider mb-2">Linked</p>
            <div className="space-y-2">
              {practitioners.map((p) => (
                <div key={p.link_id} className="flex items-center justify-between rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-tb-muted truncate">{p.email}</p>
                  </div>
                  <Button variant="ghost" onClick={() => handleUnlink(p.link_id)} className="text-xs text-red-500 hover:text-red-600">
                    Unlink
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* redeem code */}
        <div>
          <Field label="Connect code" hint="Your tax practitioner will give you this code.">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. A3BX9K2M"
              className="uppercase tracking-widest"
              onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
            />
          </Field>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-tb-brand">{success}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={handleRedeem} disabled={busy || !code.trim()}>Link</Button>
        </div>
      </div>
    </Modal>
  );
}